import { VictoryTooltip } from 'victory';

import type { Theme } from '../../../theme/themes/ThemeTypes';
import { SystemEnergyHistoryDataItem } from '../../../types/charts';
import { calculateTooltipFlyoutPosition, calculateTooltipPointerPosition } from '../../../utils/tooltipPositionUtils';

import { calculateFlyoutHeight, CHART_DIMENSIONS, TOOLTIP, TOOLTIP_POSITION } from './constants';

/**
 * Props for the CustomTooltip component
 */
interface CustomTooltipProps {
  /** Current datum from Victory chart hover event */
  datum?: {
    time?: string;
    _y?: number;
  };
  /** X coordinate of the hover point */
  x?: number;
  /** Y coordinate of the hover point */
  y?: number;
  /** Tooltip orientation (top or bottom relative to point) */
  orientation?: 'top' | 'bottom';
  /** Full dataset for the chart */
  data?: SystemEnergyHistoryDataItem[];
  /** Whether data is available to display */
  dataAvailable?: boolean;
  /** Theme object for styling */
  theme?: Theme;
  /** Chart width (default: CHART_DIMENSIONS.WIDTH) */
  width?: number;
  /** Chart height (default: CHART_DIMENSIONS.HEIGHT) */
  height?: number;
}

/**
 * Custom tooltip component for Energy Flow charts that renders multi-coloured text with flyout.
 * Displays time and all 4 energy values (Solar, Consumption, Imported, Exported)
 * with coloured dash indicators.
 *
 * The tooltip position is constrained to stay within the visible chart area.
 */
const CustomTooltip: React.FC<CustomTooltipProps> & {
  defaultEvents?: typeof VictoryTooltip.defaultEvents;
} = ({
  datum,
  x = 0,
  y = 0,
  orientation = 'bottom',
  data,
  dataAvailable,
  theme,
  width = CHART_DIMENSIONS.WIDTH,
  height = CHART_DIMENSIONS.HEIGHT,
}) => {
  if (!datum || !theme) return null;

  // Find the full data point by matching the time
  const dataPoint = dataAvailable && data ? data.find((d) => d.time === datum.time) : null;
  if (!dataPoint) return null;

  // Tooltip dimensions from constants
  const flyoutWidth = TOOLTIP.FLYOUT_WIDTH;
  const flyoutHeight = calculateFlyoutHeight();

  // Calculate constrained flyout position using utility function
  const { flyoutX, flyoutY } = calculateTooltipFlyoutPosition({
    x,
    y,
    orientation,
    flyoutWidth,
    flyoutHeight,
    chartWidth: width,
    chartHeight: height,
    margin: TOOLTIP_POSITION.CHART_MARGIN,
  });

  // Calculate pointer position using utility function
  const { pointerLength, pointerY, pointerDirection } = calculateTooltipPointerPosition({
    x,
    flyoutY,
    flyoutHeight,
    orientation,
  });

  // Destructure constants for cleaner JSX
  const { PADDING, LINE_HEIGHT, FONT_SIZE, BORDER_RADIUS, STROKE_WIDTH } = TOOLTIP;
  const pointerHalfWidth = TOOLTIP_POSITION.POINTER_HALF_WIDTH;

  return (
    <g>
      {/* Flyout background */}
      <rect
        x={flyoutX}
        y={flyoutY}
        width={flyoutWidth}
        height={flyoutHeight}
        fill={theme.colors.white}
        stroke={theme.colors.grey}
        strokeWidth={STROKE_WIDTH}
        rx={BORDER_RADIUS}
      />

      {/* Pointer triangle connecting flyout to data point */}
      <polygon
        points={`${x},${pointerY} ${x - pointerHalfWidth},${pointerY + pointerDirection * pointerLength} ${x + pointerHalfWidth},${pointerY + pointerDirection * pointerLength}`}
        fill={theme.colors.white}
        stroke={theme.colors.grey}
        strokeWidth={STROKE_WIDTH}
      />

      {/* Text labels - each row offset by LINE_HEIGHT */}
      <text
        x={flyoutX + PADDING}
        y={flyoutY + PADDING + LINE_HEIGHT * 0.7}
        textAnchor="start"
        fontSize={FONT_SIZE}
        fill={theme.colors.black}
      >
        Time: {dataPoint.time}
      </text>

      <text x={flyoutX + PADDING} y={flyoutY + PADDING + LINE_HEIGHT * 1.7} textAnchor="start" fontSize={FONT_SIZE}>
        <tspan fill={theme.colors.solarLight}>—</tspan>
        <tspan fill={theme.colors.black}> Solar: {(dataPoint['Solar generation'] || 0).toFixed(2)} kWh</tspan>
      </text>

      <text x={flyoutX + PADDING} y={flyoutY + PADDING + LINE_HEIGHT * 2.7} textAnchor="start" fontSize={FONT_SIZE}>
        <tspan fill={theme.colors.success}>—</tspan>
        <tspan fill={theme.colors.black}> Consumption: {(dataPoint.Consumption || 0).toFixed(2)} kWh</tspan>
      </text>

      <text x={flyoutX + PADDING} y={flyoutY + PADDING + LINE_HEIGHT * 3.7} textAnchor="start" fontSize={FONT_SIZE}>
        <tspan fill={theme.colors.imported}>—</tspan>
        <tspan fill={theme.colors.black}> Imported: {(dataPoint.Imported || 0).toFixed(2)} kWh</tspan>
      </text>

      <text x={flyoutX + PADDING} y={flyoutY + PADDING + LINE_HEIGHT * 4.7} textAnchor="start" fontSize={FONT_SIZE}>
        <tspan fill={theme.colors.exported}>—</tspan>
        <tspan fill={theme.colors.black}> Exported: {Math.abs(dataPoint.Exported || 0).toFixed(2)} kWh</tspan>
      </text>
    </g>
  );
};

// Replicate VictoryTooltip's default events for hover interactions
CustomTooltip.defaultEvents = (props) => VictoryTooltip.defaultEvents(props);

export default CustomTooltip;
